/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import { PcgPipelineMetrics, pipelineTimingEligible } from "@medaica/common/components/pcg-report/core"
import { PcgRecordingDisposition } from "@medaica/common/services/medaica-api-service/pcg-review-types"

/**
 * What the Accept dialog shows for one evidence domain of the selected
 * recording: whether that domain can be accepted at all, and the values the
 * reviewer is accepting.
 */
type DomainEvidence = { available: boolean; text: string }
type AcceptEvidence = { timing: DomainEvidence; structuralSound: DomainEvidence }

const NOT_AVAILABLE = "Not available from this recording"

const finite = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value)

/**
 * Availability follows the handoff viewer's Accept dialog, and the timing
 * values are what Accept sends to the Report (the report's timing roll-up
 * applies the same rules):
 * - Timing, for a recording whose guide carries the timing engine (its
 *   evidence package has an `evaluationRange` provenance block): the one
 *   canonical result, the Evaluation Range's. Available only when the engine
 *   selected a range and the package carries its heart rate; the values are
 *   the package's automated heart rate and SYS:DIA ratio, never the
 *   pipeline's whole-recording metrics;
 * - Timing, for any other recording: the analysis pipeline's timing
 *   eligibility, with the package's automated heart rate (never a BPM the
 *   reviewer confirmed) and SYS:DIA ratio, the pipeline's metrics filling any
 *   value the package lacks;
 * - Structural Sound: any usable recording — its input passed the gate and it
 *   is timing-eligible by the pipeline's rule, or its structural summary was
 *   classified or observed energy. What an accepted recording contributes to the exam-level
 *   Structural Sound finding is the report roll-up's decision, not the
 *   dialog's: an accepted recording with nothing scored leaves the card
 *   unassessed, for the clinician to assess by hand in the report.
 */
type PackageBlock = { [field: string]: unknown } | null
/** The evidence package's Evaluation Range provenance block (a timing-engine guide's). */
type EvaluationRangeProvenance = { status?: unknown; [field: string]: unknown } | null
type PackageEvidence = {
  heartRate?: PackageBlock
  systolicDiastolicTiming?: PackageBlock
  cardiacCycleTiming?: PackageBlock
  structuralSoundSummary?: PackageBlock
  [block: string]: unknown
}

const gateRejected = (pipelineMetrics: PcgPipelineMetrics | null): boolean =>
  pipelineMetrics?.gateStatus === "reject" || pipelineMetrics?.gateStatus === "rejected"

// The summary was classified (scored cycles) or observed energy whose phase
// is withheld — anything but an unassessed or unavailable one
const structuralSummaryUsable = (evidence: PackageEvidence | null): boolean => {
  const summary = evidence?.structuralSoundSummary
  if (!summary?.status || !summary.observed || summary.observed === "Not assessed") return false
  return finite(summary.maxCyclePct) || summary.phaseStatus === "pending_review"
}

const timingEvidence = (
  pipelineMetrics: PcgPipelineMetrics | null,
  evidence: PackageEvidence | null,
  evaluationRange: EvaluationRangeProvenance
): DomainEvidence => {
  const packageHr = evidence?.heartRate?.automatedValueBpm
  const packageSd = (evidence?.systolicDiastolicTiming ?? evidence?.cardiacCycleTiming)?.rawSdRatio
  // A timing-engine guide: the Evaluation Range's values or nothing
  const timingEngine = Boolean(evaluationRange)
  const available = timingEngine
    ? evaluationRange?.status === "ready" && finite(packageHr)
    : pipelineTimingEligible(pipelineMetrics)
  if (!available) return { available, text: NOT_AVAILABLE }
  const fallback = timingEngine ? null : pipelineMetrics
  const hr = finite(packageHr) ? packageHr : fallback?.hrBpm
  const sd = finite(packageSd) ? packageSd : fallback?.sdRatio
  const rate = finite(hr) ? `${Math.round(hr)} bpm` : "Rate available"
  const ratio = finite(sd) && sd > 0 ? ` · SYS:DIA 1:${(1 / sd).toFixed(1)}` : ""
  return { available, text: `${rate}${ratio}` }
}

const structuralEvidence = (
  pipelineMetrics: PcgPipelineMetrics | null,
  evidence: PackageEvidence | null
): DomainEvidence => {
  const available =
    !gateRejected(pipelineMetrics) && (pipelineTimingEligible(pipelineMetrics) || structuralSummaryUsable(evidence))
  if (!available) return { available, text: NOT_AVAILABLE }
  const summary = evidence?.structuralSoundSummary ?? {}
  const observed =
    typeof summary.observed === "string" && summary.observed ? summary.observed : "Structural Sound evidence available"
  const cycles =
    finite(summary.sysCyclePct) && finite(summary.diaCyclePct)
      ? ` · Sys ${Math.round(summary.sysCyclePct)}% / Dia ${Math.round(summary.diaCyclePct)}%`
      : ""
  return { available, text: `${observed}${cycles}` }
}

const describeAcceptEvidence = (
  pipelineMetrics: PcgPipelineMetrics | null,
  evidence: PackageEvidence | null,
  evaluationRange: EvaluationRangeProvenance = null
): AcceptEvidence => ({
  timing: timingEvidence(pipelineMetrics, evidence, evaluationRange),
  structuralSound: structuralEvidence(pipelineMetrics, evidence),
})

/**
 * The handoff viewer's exam-wide line under the review buttons: every
 * accepted recording with the evidence it was accepted for — "Pulmonic
 * (Timing + Structural), Tricuspid (Timing)" — in exam order; null when no
 * recording is accepted.
 */
const acceptedEvidenceSummary = (
  recordings: { label: string; disposition: PcgRecordingDisposition | null | undefined }[]
): string | null => {
  const accepted = recordings
    .filter((recording) => recording.disposition?.decision === "accepted")
    .map((recording) => {
      // Accepted rows saved before domains existed count for both
      const domains = recording.disposition?.acceptedDomains ?? { timing: true, structuralSound: true }
      const evidence = [domains.timing ? "Timing" : "", domains.structuralSound ? "Structural" : ""]
        .filter(Boolean)
        .join(" + ")
      return evidence ? `${recording.label} (${evidence})` : recording.label
    })
  return accepted.length ? accepted.join(", ") : null
}

export { describeAcceptEvidence, acceptedEvidenceSummary }
export type { AcceptEvidence, DomainEvidence, EvaluationRangeProvenance, PackageEvidence }
